// libs
import React, { useEffect, useMemo, useState, ErrorInfo, useCallback } from "react";

// constants
import { CACHE_POSTFIX } from "@swiggy-private/aatm-nirbhar-build/dist/constants/htmlCaching";

// components
import { ErrorPage } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/ErrorPage";
import GlobalStyled from "@swiggy-private/aatm-nirbhar-ui/dist/lib/GlobalStyled";
import { NewRelicErrorBoundary } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/NewRelic";

// dls
import { WidgetControllerContext } from "@swiggy-private/dls-react-mxn/dist";
import { FontStyle } from "@swiggy-private/dls-web-theme/dist/components/Fonts";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";

// context
import { GenericContext } from "@swiggy-private/generic-mxn-context/dist";

// types
import { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge/dist/interfaces";
import type { AppProps } from "next/app";

// helpers
import { NEW_RELIC_APPLICATION_ID } from "../constants";
import { DINEOUT_PROVIDER_THEME } from "../constants/Theme";
import { isImShell, isIos } from "../helpers";
import { sendCustomNREvViaBlaze, sendErrorBoundaryEvent } from "../helpers/newrelic";
import { Sdk, useBootstrap } from "../hooks/useBootstrap";
import { BackgroundWrapper, ShuttleAppName } from "../styles/home";
import { getAnalyticsContextValue } from "../utils/analytics";
import { closeWebview, enableBackSwipe, getSwiggyHeaders } from "../utils/bridgeSdk";
import { resolveAppTheme, getThemeQuery } from "../utils/getCollectionProviderTheme";

const CLOSE = "click here to close";
const BACK_SWIPE_ROUTES = new Set(["/irctc_pnr_search", "/crew_invite"]);
const BACK_SWIPE_ELIGIBLE_VERSION_CODE = 442;

function MyApp({ Component, pageProps, router }: AppProps) {
    // Detect if the page is a cached version
    const isCached = router.route.includes(CACHE_POSTFIX);
    const [headers, setHeaders] = useState<GetSwiggyHeadersResponsePayload>();

    // True once getSwiggyHeaders has settled (success or failure). Lets consumers
    // distinguish "headers still pending" from "headers fetched but empty" — e.g. the
    // IRCTC page waiting to decide platform without hanging on a failed bridge call.
    const [headersResolved, setHeadersResolved] = useState(false);
    const contextValue = useMemo(() => {
        const analyticsContext = getAnalyticsContextValue(headers);
        return {
            sdk: Sdk ?? undefined,
            impressionAnalytics: analyticsContext.impressionEvent,
            clickAnalytics: analyticsContext.clickEvent,
            headers,
        };
    }, [headers]);

    useEffect(() => {
        async function fetchData() {
            try {
                if (headers) {
                    return;
                }
                const headersResponse = await getSwiggyHeaders();

                setHeaders(headersResponse);
            } catch (err) {
            } finally {
                setHeadersResolved(true);
            }
        }
        fetchData();
    }, [headers]);

    useBootstrap("portal_static_page", isCached, headers);

    const isBackSwipeRoute = BACK_SWIPE_ROUTES.has(router.route.replace(CACHE_POSTFIX, ""));
    useEffect(() => {
        if (!headersResolved || !isBackSwipeRoute) {
            return;
        }

        const isBackSwipeEnabled = Number(headers?.["version-code"] ?? 0) >= BACK_SWIPE_ELIGIBLE_VERSION_CODE;
        if (!isIos(headers?.platform) || !isBackSwipeEnabled) {
            return;
        }

        enableBackSwipe(true);

        return () => {
            enableBackSwipe(false);
        };
    }, [headersResolved, isBackSwipeRoute, headers]);

    const onError = useCallback(
        (error?: Error, errorInfo?: ErrorInfo) => {
            sendErrorBoundaryEvent(error, errorInfo, headers);
        },
        [headers],
    );

    const redirectToPath = useCallback(
        ({ pathname, query }: { pathname: string; query: { [key: string]: string | number | boolean } }) => {
            router.push({
                pathname,
                query: { ...router.query, ...query },
            });
        },
        [router],
    );

    const ErrorComponentWrapper = () => <ErrorPage onCtaClick={closeWebview} ctaText={CLOSE} />;

    const genericContextValue = useMemo(
        () => ({ headers, headersResolved, sendCustomNREvViaBlaze, redirectToPath }),
        [headers, headersResolved, redirectToPath],
    );

    const [themeQuery, setThemeQuery] = useState<string | string[] | undefined>(undefined);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        setThemeQuery(
            getThemeQuery({
                routerQuery: router.query,
                search: window.location.search,
            }),
        );
    }, [router.isReady, router.query]);

    const appThemeSelection = useMemo(() => {
        const channel = headers?.["x-channel"];
        const imShell = isImShell(channel ?? "");

        return resolveAppTheme({ themeQuery, imShell });
    }, [themeQuery, headers]);

    const PageShell = appThemeSelection.providerTheme === DINEOUT_PROVIDER_THEME ? BackgroundWrapper : React.Fragment;

    return (
        <ThemeProvider
            key={appThemeSelection.providerTheme}
            theme={appThemeSelection.theme}
            colorMode={appThemeSelection.colorMode}
        >
            <PageShell>
                <NewRelicErrorBoundary
                    applicationID={NEW_RELIC_APPLICATION_ID}
                    onErrorCapture={onError}
                    ErrorComponent={ErrorComponentWrapper}
                >
                    <ShuttleAppName data-app-name={`${process.env.NEXT_PUBLIC_APP_NAME}`} />
                    <GlobalStyled />
                    <FontStyle />
                    <GenericContext.Provider value={genericContextValue}>
                        <WidgetControllerContext.Provider value={contextValue}>
                            <Component {...pageProps} />
                        </WidgetControllerContext.Provider>
                    </GenericContext.Provider>
                </NewRelicErrorBoundary>
            </PageShell>
        </ThemeProvider>
    );
}

export default MyApp;
